import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { User, AuthToken } from '../types/user';
import { fetchRefreshToken, fetchGetBalance } from '../lib/functions';
import { BALANCE_REFRESH_INTERVAL } from '../lib/config';
import log from '../utils/logger';
import { postMessageHandler, POST_MESSAGE_ACTIONS } from '../utils/postMessageHandler';

// Tipo per il context
interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  createUserFromParams: (userParam: string, cartaParam: string, tokenParam: string) => User;
  updateUserToken: (tokenData: AuthToken) => void;
  logout: () => void;
  refreshBalance: () => void;
}

// Creare il context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider component
interface UserProviderProps {
  children: ReactNode;
  onLogout?: () => void;
}

export const UserProvider = ({ children, onLogout }: UserProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const balanceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Create user from URL parameters
  const createUserFromParams = useCallback((userParam: string, cartaParam: string, tokenParam: string): User => {
    return {
      user: userParam,
      carta: cartaParam,
      token: tokenParam,
      isLogged: true,
      balance: '',
      bonus: ''
    };
  }, []);

  // Calculate when to refresh token
  const calculateRefreshDelay = (expiresIn: number): number => {
    if (expiresIn > 120) {
      // Se expiresIn > 120 secondi, refresh 60 secondi prima
      return (expiresIn - 60) * 1000;
    } else if (expiresIn > 10) {
      // Se expiresIn > 10 secondi, refresh 10 secondi prima
      return (expiresIn - 10) * 1000;
    } else {
      // Altrimenti refresh esattamente a expiresIn
      return expiresIn * 1000;
    }
  };

  // Logout function: clears user data and URL parameters
  const logout = useCallback(() => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    // Clear balance timer
    if (balanceTimerRef.current) {
      clearInterval(balanceTimerRef.current);
      balanceTimerRef.current = null;
    }
    
    // Clear user data
    setUser(null);
    
    // Call the logout callback if provided
    if (onLogout) {
      onLogout();
    }

    postMessageHandler.sendToParent(POST_MESSAGE_ACTIONS.CASINO_LOGOUT);

    log.debug('🎮 Utente disconnesso e parametri URL rimossi');
  }, [onLogout]);

  // Update user with token from launchGame response
  const updateUserToken = useCallback((tokenData: AuthToken) => {
    setUser(prevUser => {
      if (!prevUser) return null;
      
      return {
        ...prevUser,
        authToken: tokenData
      };
    });

    // Clear any existing refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Schedule token refresh
    if (tokenData.refreshToken && tokenData.expiresIn > 0) {
      const refreshDelay = calculateRefreshDelay(tokenData.expiresIn);
      const refreshTime = new Date(Date.now() + refreshDelay).toLocaleTimeString();
      
      log.info(`⏰ Token refresh scheduled for ${refreshTime} (in ${refreshDelay / 1000} seconds)`);
      
      refreshTimerRef.current = setTimeout(async () => {
        try {
          log.info('🔄 Refreshing authentication token...');
          const response = await fetchRefreshToken(tokenData.refreshToken);
          
          if (response.token) {
            log.info('✅ Token refreshed successfully');
            // Recursive call to update with new token and schedule next refresh
            updateUserToken(response.token);
          } else {
            log.error('❌ Token refresh failed: no token in response');
          }
        } catch (error: unknown) {
          log.error('❌ Token refresh error:', error);
          // In caso di errore, potrebbe essere necessario fare logout
          const errorData = error as { code?: number; data?: { esito?: number } };
          if (errorData?.code === -607 || errorData?.data?.esito === -607) {
            logout();
          }
        }
      }, refreshDelay);
    }
  }, [logout]);

  // Function to fetch and update balance
  const fetchAndUpdateBalance = useCallback(async (currentUser: User) => {
    if (!currentUser || !currentUser.authToken?.accessToken) return;
    
    try {
      const balanceData = await fetchGetBalance(currentUser);
      setUser(prevUser => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          balance: balanceData.balance,
          bonus: balanceData.bonus
        };
      });
      log.info('💰 Balance updated:', balanceData);
    } catch (error: unknown) {
      const errorData = error as { code?: number; data?: { esito?: number } };
      if (errorData?.code === -607 || errorData?.data?.esito === -607) {
        log.error('❌ Session expired while fetching balance');
        logout();
      } else {
        log.error('❌ Error fetching balance:', error);
      }
    }
  }, [logout]);

  // Function to manually refresh balance and reset timer
  const refreshBalance = useCallback(() => {
    if (!user?.authToken?.accessToken) return;
    
    log.info('💰 Manual balance refresh triggered');
    
    // Clear existing timer
    if (balanceTimerRef.current) {
      clearInterval(balanceTimerRef.current);
    }
    
    // Fetch balance immediately
    fetchAndUpdateBalance(user);
    
    // Reset the interval timer
    balanceTimerRef.current = setInterval(() => {
      setUser(currentUser => {
        if (currentUser?.authToken?.accessToken) {
          fetchAndUpdateBalance(currentUser);
        }
        return currentUser;
      });
    }, BALANCE_REFRESH_INTERVAL * 1000);
  }, [user, fetchAndUpdateBalance]);

  // Start balance refresh when user has authToken
  useEffect(() => {
    if (user?.authToken?.accessToken) {
      // Fetch balance immediately
      fetchAndUpdateBalance(user);
      
      // Clear any existing balance timer
      if (balanceTimerRef.current) {
        clearInterval(balanceTimerRef.current);
      }
      
      // Set up periodic balance refresh
      balanceTimerRef.current = setInterval(() => {
        // Get current user state in the interval
        setUser(currentUser => {
          if (currentUser?.authToken?.accessToken) {
            fetchAndUpdateBalance(currentUser);
          }
          return currentUser;
        });
      },BALANCE_REFRESH_INTERVAL * 1000);
      
      log.info(`💰 Balance refresh started (every ${BALANCE_REFRESH_INTERVAL} seconds)`);
    } else {
      // Clear balance timer if no authToken
      if (balanceTimerRef.current) {
        clearInterval(balanceTimerRef.current);
        balanceTimerRef.current = null;
      }
    }
    
    return () => {
      if (balanceTimerRef.current) {
        clearInterval(balanceTimerRef.current);
        balanceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.authToken?.accessToken]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (balanceTimerRef.current) {
        clearInterval(balanceTimerRef.current);
      }
    };
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      createUserFromParams,
      updateUserToken,
      logout,
      refreshBalance
    }}>
      {children}
    </UserContext.Provider>
  );
};

// Hook personalizzato per usare il context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser deve essere usato dentro UserProvider');
  }
  return context;
};